import React, { useState, useEffect } from 'react';
import { Condominium } from '../types';
import { 
  Save, 
  X, 
  MapPin,
  Building,
  CheckSquare,
  Square,
  Upload,
  Image as ImageIcon,
  Trash2,
  Camera,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { uploadImageWithWatermark, processImageFile } from '../utils/watermark';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para imagem sortável
interface SortableImageItemProps {
  image: any;
  onRemove: (id: string) => void;
}

const SortableImageItem: React.FC<SortableImageItemProps> = ({ image, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative group"
    >
      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
        <img
          src={image.preview || image.url}
          alt={image.name}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Handle de arraste */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded cursor-grab hover:bg-opacity-70 transition-opacity z-20"
        title="Arrastar para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      {/* Overlay com ações */}
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
        <button
          type="button"
          onClick={() => onRemove(image.id!)}
          className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
          title="Remover imagem"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Nome do arquivo */}
      <p className="text-xs text-gray-500 mt-1 truncate" title={image.name}>
        {image.name}
      </p>
    </div>
  );
};

interface CondominiumFormProps {
  condominium?: Condominium;
  onSave: (condominium: Condominium) => void;
  onCancel: () => void;
}

const CondominiumForm: React.FC<CondominiumFormProps> = ({ condominium, onSave, onCancel }) => {
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [formData, setFormData] = useState<Partial<Condominium>>({
    id: '',
    name: '',
    address: {
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: 'SP',
      postalCode: ''
    },
    features: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    images: []
  });

  // Configuração dos sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Lista completa de características conforme especificado (ordenadas alfabeticamente dentro de cada categoria)
  const featureCategories = [
    {
      name: 'Lazer',
      icon: '🎯',
      features: [
        { value: 'Gym', label: 'Academia' },
        { value: 'Recreation Area', label: 'Área de lazer' },
        { value: 'Pool Bar', label: 'Bar na piscina' },
        { value: 'Library', label: 'Biblioteca' },
        { value: 'Toys Place', label: 'Brinquedoteca' },
        { value: 'Football Field', label: 'Campo de futebol' },
        { value: 'Golf Field', label: 'Campo de golfe' },
        { value: 'Beauty Center', label: 'Centro de estética' },
        { value: 'Children Care', label: 'Children care' },
        { value: 'BBQ', label: 'Churrasqueira' },
        { value: 'Media Room', label: 'Cinema' },
        { value: 'Coffee Shop', label: 'Coffee shop' },
        { value: 'Coworking', label: 'Coworking' },
        { value: 'Beauty Room', label: 'Espaço de beleza' },
        { value: 'Fitness Room', label: 'Espaço fitness' },
        { value: 'Gourmet Area', label: 'Espaço gourmet' },
        { value: 'Pet Space', label: 'Espaço Pet' },
        { value: 'Teen Space', label: 'Espaço teen' },
        { value: 'Green space / Park', label: 'Espaço verde / Parque' },
        { value: 'Zen Space', label: 'Espaço zen' },
        { value: 'Reflective Pool', label: 'Espelhos d\'água' },
        { value: 'Orchid Place', label: 'Orquidário' },
        { value: 'Pool', label: 'Piscina' },
        { value: 'Heated Pool', label: 'Piscina aquecida' },
        { value: 'Covered Pool', label: 'Piscina coberta' },
        { value: 'Childrens Pool', label: 'Piscina infantil' },
        { value: 'Adult Pool', label: 'Piscina para adulto' },
        { value: 'Private Pool', label: 'Piscina privada' },
        { value: 'Semi Olympic Pool', label: 'Piscina semi-olímpica' },
        { value: 'Jogging track', label: 'Pista de cooper' },
        { value: 'Skate Lane', label: 'Pista de skate' },
        { value: 'Playground', label: 'Playground' },
        { value: 'Pomar', label: 'Pomar' },
        { value: 'Square', label: 'Praça' },
        { value: 'Sand Pit', label: 'Quadra de areia' },
        { value: 'Indoor Soccer', label: 'Quadra de futebol' },
        { value: 'Sports Court', label: 'Quadra poliesportiva' },
        { value: 'Squash', label: 'Quadra de squash' },
        { value: 'Tennis court', label: 'Quadra de tênis' },
        { value: 'Redario', label: 'Redário' },
        { value: 'Massage Room', label: 'Sala de massagem' },
        { value: 'Meeting Room', label: 'Sala de reunião' },
        { value: 'Convention Hall', label: 'Salão de convenção' },
        { value: 'Party Room', label: 'Salão de Festas' },
        { value: 'Game room', label: 'Salão de jogos' },
        { value: 'Sauna', label: 'Sauna' },
        { value: 'Solarium', label: 'Solarium' },
        { value: 'Spa', label: 'Spa' }
      ]
    },
    {
      name: 'Segurança',
      icon: '🔒',
      features: [
        { value: 'Security Camera', label: 'Câmera de segurança' },
        { value: 'TV Security', label: 'Circuito de segurança' },
        { value: 'Fenced Yard', label: 'Condomínio fechado' },
        { value: 'Armored Security Cabin', label: 'Guarita blindada' },
        { value: 'Entrance Hall', label: 'Hall de entrada' },
        { value: 'Concierge 24h', label: 'Portaria 24h' },
        { value: 'Reception room', label: 'Recepção' },
        { value: 'Patrol', label: 'Ronda/Vigilância' },
        { value: 'Security Guard on Duty', label: 'Segurança 24h' }
      ]
    },
    {
      name: 'Conveniência',
      icon: '🏢',
      features: [
        { value: 'Disabled Access', label: 'Acesso para deficientes' },
        { value: 'Bicycles Place', label: 'Bicicletário' },
        { value: 'Carregador eletrônico para carro e bicicleta', label: 'Carregador eletrônico para carro e bicicleta' },
        { value: 'Eco Garbage Collector', label: 'Coleta seletiva de lixo' },
        { value: 'Coverage', label: 'Cobertura coletiva' },
        { value: 'Smart Condominium', label: 'Condomínio inteligente' },
        { value: 'Eco Condominium', label: 'Condomínio sustentável' },
        { value: 'Elevator', label: 'Elevador' },
        { value: 'Guest Parking', label: 'Estacionamento para visitante' },
        { value: 'Generator', label: 'Gerador elétrico' },
        { value: 'Helipad', label: 'Heliponto' },
        { value: 'Valet Parking', label: 'Manobrista' },
        { value: 'Close to hospitals', label: 'Perto de hospitais' },
        { value: 'Close to schools', label: 'Perto de Escolas' },
        { value: 'Near Shopping Center', label: 'Perto de Shopping' },
        { value: 'Close to shopping centers', label: 'Perto de Shopping Center' },
        { value: 'Close to public transportation', label: 'Perto de transporte público' },
        { value: 'Close to main roads/avenues', label: 'Perto de vias de acesso' },
        { value: 'Dress Room2', label: 'Vestiário para diaristas' }
      ]
    }
  ];

  // Função para formatar CEP
  const formatCep = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) {
      return numbers;
    }
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Função para buscar endereço por CEP
  const fetchAddressByCep = async () => {
    const cep = formData.address?.postalCode?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      if (formData.address?.postalCode && formData.address.postalCode.length > 0) {
        setCepError('CEP deve ter 8 dígitos');
      }
      return;
    }

    setCepLoading(true);
    setCepError('');

    try {
      // Tentar múltiplas URLs da API ViaCEP
      const urls = [
        `https://viacep.com.br/ws/${cep}/json/`,
        `https://cdn.apicep.com/file/apicep/${cep}.json`,
        `https://brasilapi.com.br/api/cep/v1/${cep}`
      ];
      
      let response;
      let data;
      
      // Tentar cada URL até uma funcionar
      for (const url of urls) {
        try {
          response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          });
          
          if (response.ok) {
            data = await response.json();
            break;
          }
        } catch (urlError) {
          console.log(`Erro na URL ${url}:`, urlError);
          continue;
        }
      }
      
      if (!data) {
        throw new Error('Todas as APIs de CEP falharam');
      }
      

      // Verificar se o CEP foi encontrado (diferentes APIs têm formatos diferentes)
      if (data.erro || data.error || !data.cep) {
        setCepError('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address!,
          state: data.uf || data.state || prev.address?.state || 'SP',
          city: data.localidade || data.city || prev.address?.city || '',
          neighborhood: data.bairro || data.district || prev.address?.neighborhood || '',
          street: data.logradouro || data.address || prev.address?.street || '',
          number: prev.address?.number || '',
          postalCode: prev.address?.postalCode || ''
        }
      }));

    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setCepError('CEP não encontrado ou serviço temporariamente indisponível');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCep(e.target.value);
    handleInputChange('address.postalCode', formatted);
    setCepError('');
    
    // Auto-buscar quando CEP estiver completo
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setTimeout(() => {
        fetchAddressByCep();
      }, 500); // Delay para evitar muitas requisições
    } else {
      // Clear any previous address data if CEP is incomplete
      if (cleanCep.length > 0 && cleanCep.length < 8) {
        setCepError('CEP deve ter 8 dígitos');
      } else {
        setCepError('');
      }
    }
  };

  // Função para gerar ID único
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Função para validar arquivo de imagem
  const validateImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setUploadError('Apenas arquivos JPG, PNG e WEBP são permitidos');
      return false;
    }

    if (file.size > maxSize) {
      setUploadError('Arquivo deve ter no máximo 5MB');
      return false;
    }

    return true;
  };

  // Função para processar arquivos com marca d'água
  const processFiles = async (files: FileList) => {
    setUploadError('');
    setIsProcessingImages(true);
    const newImages: any[] = [];

    console.log('🔧 CondominiumForm: Iniciando processamento de imagens...');

    for (const file of Array.from(files)) {
      if (validateImageFile(file)) {
        try {
          console.log('📸 CondominiumForm: Aplicando marca d\'água na imagem:', file.name);
          
          // Processa o arquivo e aplica marca d'água
          const processedFile = await processImageFile(file);
          const watermarkedUrl = await uploadImageWithWatermark(processedFile);
          
          const id = generateId();
          
          newImages.push({
            id,
            url: watermarkedUrl,
            preview: watermarkedUrl,
            name: file.name,
            watermarked: true
          });

          console.log('✅ CondominiumForm: Marca d\'água aplicada com sucesso em:', file.name);
        } catch (error) {
          console.error('❌ CondominiumForm: Erro ao aplicar marca d\'água:', error);
          
          // Em caso de erro, usar a imagem original
          const id = generateId();
          const preview = URL.createObjectURL(file);
          
          newImages.push({
            id,
            file,
            preview,
            name: file.name
          });
        }
      }
    }

    if (newImages.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
      
      console.log('✅ CondominiumForm: Imagens adicionadas ao formData com sucesso!');
      console.log('📈 CondominiumForm: Total de imagens no formData:', (formData.images?.length || 0) + newImages.length);
    } else {
      console.log('⚠️ CondominiumForm: Nenhuma imagem foi processada');
    }
    
    setIsProcessingImages(false);
  };

  // Handlers para drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFiles(e.dataTransfer.files);
    }
  };

  // Handler para input file
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFiles(e.target.files);
    }
  };

  // Função para manipular drag and drop das imagens
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const images = prev.images || [];
        const oldIndex = images.findIndex(img => img.id === active.id);
        const newIndex = images.findIndex(img => img.id === over?.id);

        return {
          ...prev,
          images: arrayMove(images, oldIndex, newIndex)
        };
      });
    }
  };

  // Função para remover imagem
  const removeImage = (imageId: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter(img => img.id !== imageId) || []
    }));
  };

  // Cleanup URLs quando componente desmonta
  useEffect(() => {
    return () => {
      formData.images?.forEach(img => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (condominium) {
      const condominiumWithImages = {
        ...condominium,
        images: condominium.images?.map(img => ({
          id: img.id,
          name: img.name,
          url: img.url,
          preview: img.url || img.preview // Usar URL se existir, senão preview local
        })) || []
      };
      setFormData(condominiumWithImages);
    }
  }, [condominium]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof Condominium] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const toggleFeature = (featureValue: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.includes(featureValue)
        ? prev.features.filter(f => f !== featureValue)
        : [...(prev.features || []), featureValue]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const condominiumData: Condominium = {
      ...formData,
      id: formData.id || Date.now().toString(),
      updatedAt: new Date(),
    } as Condominium;

    onSave(condominiumData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {condominium ? 'Editar Condomínio' : 'Novo Condomínio'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Informações Básicas
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Condomínio *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ex: Residencial Jardim das Flores"
              />
            </div>
          </div>

          {/* Localização */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Localização
            </h3>
            
            <div className="space-y-4">
              {/* CEP primeiro */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CEP *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={formData.address?.postalCode}
                    onChange={handleCepChange}
                    onBlur={fetchAddressByCep}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="01234-567"
                    maxLength={9}
                  />
                  {cepLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                    </div>
                  )}
                </div>
                {cepError && (
                  <p className="text-red-500 text-sm mt-1">{cepError}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address?.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="SP"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address?.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="São Paulo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bairro *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address?.neighborhood}
                    onChange={(e) => handleInputChange('address.neighborhood', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Jardim Paulista"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address?.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Rua das Palmeiras"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address?.number}
                    onChange={(e) => handleInputChange('address.number', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="1000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Upload de Imagens */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <ImageIcon className="w-5 h-5 mr-2" />
              Imagens do Condomínio
            </h3>
            
            {/* Área de Upload */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
                
                <div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Arraste as imagens aqui
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    ou clique no botão abaixo para selecionar
                  </p>
                  
                  <label className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer inline-flex items-center space-x-2">
                    <Upload className="w-5 h-5" />
                    <span>Selecionar Imagens</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </label>
                </div>
                
                <p className="text-xs text-gray-400">
                  Formatos aceitos: JPG, PNG, WEBP • Máximo: 5MB por imagem
                </p>
              </div>
            </div>

            {/* Erro de Upload */}
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{uploadError}</p>
              </div>
            )}

            {/* Preview das Imagens com Drag and Drop */}
            {formData.images && formData.images.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-md font-medium text-gray-700">
                  Imagens Selecionadas ({formData.images.length})
                  <span className="text-sm text-gray-500 ml-2">
                    Arraste para reordenar
                  </span>
                </h4>
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={formData.images.map(img => img.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {formData.images.map((image) => (
                        <SortableImageItem
                          key={image.id}
                          image={image}
                          onRemove={removeImage}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {/* Indicador de processamento */}
            {isProcessingImages && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <p className="text-blue-700 text-sm">Aplicando marca d'água nas imagens...</p>
                </div>
              </div>
            )}
          </div>

          {/* Características */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Características do Condomínio
            </h3>
            <p className="text-sm text-gray-600">
              Selecione todas as características que o condomínio possui:
            </p>
            
            <div className="space-y-6 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {featureCategories.map((category) => (
                <div key={category.name} className="space-y-3">
                  <h4 className="text-md font-semibold text-gray-800 flex items-center space-x-2 border-b border-gray-200 pb-2">
                    <span className="text-lg">{category.icon}</span>
                    <span>{category.name}</span>
                    <span className="text-sm text-gray-500 font-normal">
                      ({category.features.filter(f => formData.features?.includes(f.value)).length}/{category.features.length})
                    </span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {category.features.map((feature) => {
                      const isSelected = formData.features?.includes(feature.value);
                      return (
                        <label
                          key={feature.value}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                        >
                          <button
                            type="button"
                            onClick={() => toggleFeature(feature.value)}
                            className="flex-shrink-0"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-4 h-4 text-orange-500" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          <span className="text-sm text-gray-700">
                            {feature.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-sm text-gray-500">
              {formData.features?.length || 0} características selecionadas
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{condominium ? 'Atualizar' : 'Salvar'} Condomínio</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CondominiumForm;
